import React, {Dispatch, MutableRefObject, useEffect, useState, useCallback} from 'react';
import {Card, Divider, Label, SelectBox, Space} from '@mc/ui-v4';
import * as IconSpec from '@mc/react-ui-icons';
import {ToolbarButton} from './ToolbarButton';
import {getToolInfo} from '../../helpers/ToolsServiceMapper';
import {
    EditorLayersMode,
    EditorTool,
    EditorToolType,
    ExtendedEditorToolDefinition,
    ExtendedEditorToolType,
    GroupFeaturePointer,
    LayerId,
    MapEditorActions,
    ViewerToolType,
} from '@mc/react-mapio';
import {ETriggerAction, Trigger} from '../../helpers/Trigger';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {WaitForQueryData} from '@mc/react-app-v4';
import {newItemToolbarChildDef, ToolbarGroupId} from './ToolbarConst';
import {handleToolAction} from './ToolActions';
import {LayerVO} from './../../api';
import {ToolbarDropdown} from './ToolbarDropdown';
import {useMapioContext} from '../../mapioContext';

type Props = {
    mapRef: MutableRefObject<MapEditorActions>;
    tool: ExtendedEditorToolDefinition;
    setSelectedToolKey: Dispatch<React.SetStateAction<number>>;
    horizontal: boolean;
    disabled?: boolean;
    setOtherToolsTrigger: Dispatch<React.SetStateAction<Trigger>>;
    selectedDetailFeature: GroupFeaturePointer[];
    clearSelect?: () => void;
    writableLayers: LayerId[];
    activeTool: EditorToolType;
    toolsEnabled: EditorToolType[];
    layersMode: EditorLayersMode;
};

/**
 * Nadefinované skupiny toolů, které se mají vykreslovat jako dropdowny
 */
const DROPDOWN_GROUPS = new Set([
    ToolbarGroupId.SELECT_GROUP,
    ToolbarGroupId.COLLECTION_ADD,
    ToolbarGroupId.COLLECTION_MANAGE,
    ToolbarGroupId.COLLECTION_CUTOUT,
    ToolbarGroupId.ACTIONS,
]);

export const ToolbarDetail: React.FC<Props> = ({
    tool,
    mapRef,
    horizontal,
    clearSelect,
    setOtherToolsTrigger,
    selectedDetailFeature,
    writableLayers,
    disabled,
    activeTool,
    toolsEnabled,
    layersMode,
    setSelectedToolKey,
}) => {
    const BUTTON_HEIGHT = 28;
    const SEPARATOR_HEIGHT = 1;
    const PADDING_HEIGHT = 7;

    //vybraný nástroj DETAILU => neplést s vybraným nástroje  postraního panelu (ie. "wrapperu"), ten je v props tool
    const [selectedTool, setSelectedTool] = useState(null);
    //druhotný nástroj, používá se POUZE pokud je v detailu nástroj typu GROUP s rememberSecondary=true
    //tj. pokud je nutné někdy restartovat vybraný nástoj po akci jiného nástroje ze STEJNÉHO detailu
    const [secondaryTool, setSecondaryTool] = useState(null);
    //následující staty jsou potřebné pro nástroj typu NEW_ITEM
    const {
        value: {insertionLayerId},
        setInsertionLayerId,
    } = useMapioContext();

    const layersQuery = useLayersConfig();

    const containsActiveTool = (items, activeTool) => {
        for (const item of items) {
            if (item === activeTool) {
                return true;
            }
            if (item?.items?.length > 0) {
                // Rekurzivní kontrola vnořených items
                if (containsActiveTool(item.items, activeTool)) {
                    return true;
                }
            }
        }
        return false;
    };

    const setFirstItem = tool => {
        //pokračuj pouze pokud vybraný nástroj obsahuje nějaké "subtooly"
        //POKUD JE Z DANÉ SEKCE JIŽ NĚCO VYBRÁNO, NEPŘEPISUJ STATE
        if (tool?.items && tool.items.length > 0 && !tool.items.includes(activeTool)) {
            if (containsActiveTool(tool.items, activeTool)) {
                return;
            }
            const toolInfo = getToolInfo(tool.items[0]);

            if (toolInfo.type === ExtendedEditorToolType.GROUP) {
                //Typ GROUP je jenom wrapper -> pracuj dale s childama
                setFirstItem(tool.items[0]);
            } else if (toolInfo.type === ExtendedEditorToolType.NEW_ITEM) {
                setFirstItemNEW_ITEM();
            } else if (selectedTool?.type !== toolInfo.type) {
                //Nastav prvni nastroj
                setSelectedTool(toolInfo);
                mapRef.current.activateToolWithType(toolInfo.type);
            }
        }
    };

    const setFirstItemNEW_ITEM = () => {
        const vrstvy = layersQuery.data;
        if (vrstvy) {
            const allowedNewItemTools = newItemToolbarChildDef.filter(item => isToolAllowed(vrstvy, item));

            // Pokud je aktuálně aktivní nástroj již jedním z povolených nástrojů NEW_ITEM, nepřepisuj ho
            if (activeTool && allowedNewItemTools.includes(activeTool as any)) {
                setSelectedTool(getToolInfo(activeTool as any));
                return;
            }

            if (insertionLayerId && allowedNewItemTools[0]) {
                const toolInfoNew = getToolInfo(allowedNewItemTools[0]);
                if (toolInfoNew && !allowedNewItemTools.includes(selectedTool?.type) && activeTool !== toolInfoNew.type) {
                    mapRef.current.activateToolWithType(toolInfoNew.type);
                    setSelectedTool(toolInfoNew);
                }
            } else if (writableLayers?.length > 0) {
                setInsertionLayerId(writableLayers[0]);
                mapRef.current.setInsertionLayer(writableLayers[0]);
            }
        }
    };

    useEffect(() => {
        if (horizontal) {
            //u horizontálního detailu je nutné nastavit první "vybratelný" tool
            setFirstItem(tool);
            //každý tool má defaultně secondary prázdný
            setSecondaryTool(null);
        }
        // JSON.stringify() je v tomto případě zcela oprávněný - tvrdí to přímo vývojář Reactu
        // https://github.com/facebook/react/issues/14476#issuecomment-471199055
    }, [tool?.id, horizontal, layersQuery?.data, insertionLayerId, JSON.stringify(writableLayers)]);

    // Validace zvolené vkládací vrstvy MIMO render. Pokud aktuální insertionLayerId není mezi
    // zapisovatelnými vrstvami (např. po zapnutí/vypnutí vrstvy v katalogu), vybere se první dostupná.
    // Dříve běželo přímo v renderu NEW_ITEM panelu (setInsertionLayer volal synchronně do MapEditoru
    // během renderu Toolbaru) → "Cannot update MapEditor while rendering Toolbar" + "Too many re-renders".
    useEffect(() => {
        if (writableLayers?.length && (!insertionLayerId || !writableLayers.includes(insertionLayerId))) {
            setInsertionLayerId(writableLayers[0]);
            mapRef.current?.setInsertionLayer(writableLayers[0]);
        }
    }, [insertionLayerId, JSON.stringify(writableLayers)]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset secondaryTool při DELETE řešíme mimo render (dříve setSecondaryTool přímo v render-fázi).
    useEffect(() => {
        if (secondaryTool?.type === EditorToolType.DELETE) {
            setSecondaryTool(null);
        }
    }, [secondaryTool]);

    const isToolAllowed = useCallback(
        (
            vrstvy: {
                [key: string]: LayerVO;
            },
            tool,
        ): boolean => {
            const allowedGeometry = vrstvy[insertionLayerId]?.allowedGeometry;

            //pokud není allowed geometry definovaná, vše je povolené
            if (!allowedGeometry) return true;

            const allowPoint = allowedGeometry.includes('POINT');
            const allowLine = allowedGeometry.includes('LINE');
            const allowPoly = allowedGeometry.includes('POLYGON');

            if (allowPoint && tool.includes('POINT')) return true;
            if (allowLine && tool.includes('LINE')) return true;
            if (allowPoly && (tool.includes('POLYGON') || tool.includes('CIRCLE') || tool.includes('RECTANGLE'))) return true;
        },
        [insertionLayerId],
    );

    const isActive = useCallback(
        (selected, toolInfo) => {
            if (activeTool && toolInfo.type) {
                return activeTool === toolInfo.type;
            } else return selected?.id ? selected.id === toolInfo.id : selected?.type === toolInfo?.type;
        },
        [activeTool],
    );

    // Pokud rodič (MapPage) předal pole `toolsEnabled`, použijeme ho jako jediný zdroj pravdy.
    // Pokud je `toolsEnabled` undefined nebo prázdné, interpretujeme to jako "ignoruj filtr" => povolit vše.
    const isEnabledForTool = useCallback(
        toolInfo => {
            // Viewer nástroje jsou povoleny vždy
            if (toolInfo?.type && Object.values(ViewerToolType).includes(toolInfo.type)) return true;
            const filterActive = Array.isArray(toolsEnabled) && toolsEnabled.length > 0;
            if (!filterActive) return true;
            if (!toolInfo?.type) return false;
            return toolsEnabled.includes(toolInfo.type);
        },
        [toolsEnabled],
    );

    const handlePostToMap = useCallback(
        (toolInfo, extraParams?: {addToCollection?: boolean}) => {
            if (activeTool === toolInfo.type) {
                setSelectedToolKey(null);
            }
            handleToolAction({
                toolType: toolInfo.type,
                mapRef,
                selectedDetailFeature,
                extraParams,
                handleDefault: () => mapRef.current.activateToolWithType(toolInfo.type, extraParams as any),
            });
        },
        [activeTool, setSelectedToolKey, mapRef, selectedDetailFeature],
    );

    const handleVerticalToolClick = useCallback(
        toolInfo => {
            if ([EditorToolType.EXPORT_LAYERS, EditorToolType.EXPORT_FEATURES, EditorToolType.PRINT].includes(toolInfo.type)) {
                if ([EditorToolType.EXPORT_LAYERS, EditorToolType.EXPORT_FEATURES].includes(toolInfo.type) || activeTool === EditorToolType.PRINT) {
                    mapRef.current.activateToolWithType(null);
                }
                setOtherToolsTrigger({
                    actionToTrigger: ETriggerAction.SHOW,
                    tool: toolInfo.type,
                });
                setSelectedToolKey(null);
            }

            if (![EditorToolType.EXPORT_LAYERS, EditorToolType.EXPORT_FEATURES].includes(toolInfo.type)) {
                mapRef.current.activateToolWithType(toolInfo.type);
            }
            if (clearSelect) clearSelect();
        },
        [activeTool, mapRef, setOtherToolsTrigger, setSelectedToolKey, clearSelect],
    );

    const renderVerticalDetail = () => {
        return (
            <Card
                mode={'view'}
                className={'toolbar-detail card-border drop-shadow'}
                style={{
                    top: tool.order
                        ? `${tool.order * (BUTTON_HEIGHT + PADDING_HEIGHT) + PADDING_HEIGHT + tool.section * (PADDING_HEIGHT + SEPARATOR_HEIGHT)}px`
                        : '0px',
                }}
            >
                <Space className={'p-1'} vertical>
                    {tool.items.map(item => {
                        const toolInfo = getToolInfo(item);
                        const localEnabled = isEnabledForTool(toolInfo);
                        return (
                            <>
                                {item === EditorTool.SEPARATOR ? (
                                    <Divider key={item} type={'line'} horizontal />
                                ) : (
                                    <ToolbarButton
                                        disabled={disabled || !localEnabled}
                                        classname={'w-100'}
                                        onClick={() => handleVerticalToolClick(toolInfo)}
                                        active={toolInfo.active}
                                        icon={IconSpec[toolInfo.icon]}
                                        intent={toolInfo.type === EditorToolType.DELETE ? 'danger' : 'primary'}
                                    >
                                        {toolInfo.name}
                                    </ToolbarButton>
                                )}
                            </>
                        );
                    })}
                </Space>
            </Card>
        );
    };

    const renderHorizontalDetail = () => {
        return (
            <Card
                mode={'edit'}
                className={'toolbar-detail card-border drop-shadow'}
                // MOMENTALNE CHCEME ZOBRAZOVAT POUZE VERTIKALNI DETAILY VEDLE TLACITEK
                // style={{
                //     top: tool.order
                //         ? `${tool.order * (BUTTON_HEIGHT + PADDING_HEIGHT) + PADDING_HEIGHT + tool.section * (PADDING_HEIGHT + SEPARATOR_HEIGHT)}px`
                //         : '0px',
                // }}
            >
                <Space className={'p-1'}>
                    {tool.items.map((item, key) => {
                        return renderHorizontalItems(item, key?.toString());
                    })}
                </Space>
            </Card>
        );
    };

    const renderHorizontalItems = (item: ExtendedEditorToolDefinition | EditorTool | EditorToolType, key: string, rememberSecondary = false) => {
        const toolInfo = getToolInfo(item);
        // Enabled rozhodujeme pouze podle props.toolsEnabled (předpřipravené v MapPage)
        const localEnabled = isEnabledForTool(toolInfo);
        if (item === EditorTool.SEPARATOR) return <Divider key={key} type={'line'} />;
        switch (toolInfo.type) {
            case ExtendedEditorToolType.NEW_ITEM:
                return (
                    <div key={key}>
                        <WaitForQueryData queries={[layersQuery]}>
                            {() => {
                                const vrstvy = layersQuery.data;
                                const allowedNewItemTools = newItemToolbarChildDef.filter(item => isToolAllowed(vrstvy, item));
                                // Auto-výběr platné vkládací vrstvy řeší useEffect výše (mimo render),
                                // aby nedocházelo k setState/volání do MapEditoru během renderu (infinite-render).
                                return (
                                    <Space>
                                        {layersMode !== EditorLayersMode.OVERLAY && (
                                            <div className={'select-tool'}>
                                                <Label>{toolInfo.name}</Label>
                                                <SelectBox
                                                    disabled={disabled}
                                                    items={
                                                        writableLayers
                                                            ? writableLayers?.map(item => {
                                                                  return {
                                                                      id: item,
                                                                      name: vrstvy[item]?.label,
                                                                  };
                                                              })
                                                            : []
                                                    }
                                                    value={insertionLayerId || ''}
                                                    onChange={val => {
                                                        setInsertionLayerId(val);
                                                        mapRef.current.setInsertionLayer(val);
                                                    }}
                                                    useIdAsValue
                                                />
                                            </div>
                                        )}
                                        {insertionLayerId && (
                                            <>
                                                {layersMode !== EditorLayersMode.OVERLAY && <Divider key={key} type={'line'} />}
                                                {allowedNewItemTools.map((subtoolDef, subkey) => {
                                                    return renderHorizontalItems(subtoolDef, `${key}_${subkey}`);
                                                })}
                                            </>
                                        )}
                                    </Space>
                                );
                            }}
                        </WaitForQueryData>
                    </div>
                );
            case ExtendedEditorToolType.GROUP:
                if (DROPDOWN_GROUPS.has(toolInfo.id)) {
                    return (
                        <div key={key}>
                            <Label className={'text-center'}>{toolInfo.name}</Label>
                            <ToolbarDropdown
                                items={toolInfo.items}
                                disabled={disabled || toolInfo.items.length === 0}
                                activeTool={activeTool as EditorToolType}
                                toolsEnabled={toolsEnabled}
                                horizontal={toolInfo.horizontal}
                                onClick={(tool: any) => {
                                    //zpracování secondary tool
                                    if (tool?.type === selectedTool?.type) {
                                        setSelectedTool(null);
                                    } else {
                                        setSelectedTool(tool);
                                    }
                                    //poslání eventu mapě/angularu – u skupiny "Přidat do kolekce" předáme příznak,
                                    //aby engine přidal zákres do vybraného objektu (povýšení na kolekci) místo nového objektu
                                    handlePostToMap(tool, toolInfo.id === ToolbarGroupId.COLLECTION_ADD ? {addToCollection: true} : undefined);
                                }}
                            />
                        </div>
                    );
                }
                return (
                    <div key={key}>
                        <Label className={'text-center'}>{toolInfo.name}</Label>
                        <Space className={'pt-auto'}>
                            {toolInfo?.items.map((subitem: EditorTool | EditorToolType, subkey: string) => {
                                return renderHorizontalItems(subitem, subkey, toolInfo.rememberSecondary);
                            })}
                        </Space>
                    </div>
                );
            default:
                return (
                    <div key={key} className="d-flex align-items-center">
                        <ToolbarButton
                            title={toolInfo.name}
                            disabled={disabled || !localEnabled}
                            classname={'py-1'}
                            onClick={() => {
                                //zpracování secondary tool
                                if (rememberSecondary) {
                                    if (toolInfo?.type === secondaryTool?.type) {
                                        //setSecondaryTool(null); //TODO až se vyřeší angularově secondary tool tak toto odkomentovat a odmazat setSelectedTool na dalším řádku
                                        setSelectedTool(null);
                                    } else {
                                        //setSecondaryTool(toolInfo); //TODO až se vyřeší angularově secondary tool tak toto odkomentovat a odmazat setSelectedTool na dalším řádku
                                        setSelectedTool(toolInfo);
                                    }
                                } else {
                                    if (toolInfo?.type === selectedTool?.type) {
                                        setSelectedTool(null);
                                    } else {
                                        setSelectedTool(toolInfo);
                                    }
                                }

                                //poslání eventu mapě/angularu
                                handlePostToMap(toolInfo);
                            }}
                            active={isActive(
                                /*TODO až se vyřeší secondary tool tak odkomentovat toto: rememberSecondary ? secondaryTool :*/
                                selectedTool,
                                toolInfo,
                            )}
                            vertical
                            size={'lg'}
                            icon={IconSpec[toolInfo.icon]}
                            intent={toolInfo.type === EditorToolType.DELETE ? 'danger' : 'primary'}
                        />
                    </div>
                );
        }
    };

    return horizontal ? renderHorizontalDetail() : renderVerticalDetail();
};
